import { useEffect, useId, useRef, useState, type FormEvent } from 'react';
import { useBlocker, useLocation, useNavigate } from 'react-router';
import type { ContactValues, SyncOp } from '@wr/shared';
import {
  ContactForm,
  EMPTY_CONTACT,
  toFieldErrors,
  type ContactDraft,
  type FieldErrors,
} from '../components/ContactForm.js';
import { useToast } from '../components/Toast.js';
import { Dialog, Notice } from '../components/ui.js';
import { useOffline } from '../offline/context.js';
import { newId } from '../offline/ids.js';
import { validateContact } from '../lists.js';
import { useSignedIn } from '../session.js';
import {
  IsThereAProject,
  KnownContact,
  ProjectBlock,
  focusFirst,
  leadOps,
  projectErrors,
  knownContactId,
  projectFrom,
  useKnownContact,
  type ProjectDraft,
  type ProjectErrors,
} from './leads/Project.js';
import { useStages } from './leads/stages.js';
import { useAskEvent } from './capture/AskEvent.js';

const BLOCK_1 = ['first_name', 'last_name', 'email', 'company', 'job_title', 'country'];

/**
 * Add contact (brief section 6.2, decision of Nov 2, 2026): block 1, the
 * person, with Already a contact as soon as the email is typed; then Is
 * there a project? No: Save, a contact. Yes: block 2, the project, opens
 * on the same screen, then Save creates the contact, the lead and their
 * link at once. More info, optional and folded, comes last. Saved on the
 * device first, then sent by itself (offline first).
 */
export function AddContact() {
  const { user, can } = useSignedIn();
  const { save, store } = useOffline();
  const askEvent = useAskEvent();
  const toast = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const stages = useStages();
  const canLead = can('leads.create');
  const [draft, setDraft] = useState<ContactDraft>(EMPTY_CONTACT);
  // Add lead's "New person" opens it on Yes.
  const [project, setProject] = useState<boolean>(
    () => (location.state as { kind?: string } | null)?.kind === 'lead' && canLead,
  );
  const [answers, setAnswers] = useState<ProjectDraft>(() => projectFrom());
  const [countryChosen, setCountryChosen] = useState(false);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [projectErrs, setProjectErrs] = useState<ProjectErrors>({});
  const [moreOpen, setMoreOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [failed, setFailed] = useState<string | null>(null);
  const formRef = useRef<HTMLFormElement>(null);
  const saved = useRef(false);
  const known = useKnownContact(draft.email);

  // The installation country follows the person's country until it is chosen.
  const shown: ProjectDraft = countryChosen
    ? answers
    : { ...answers, installation_country: draft.country };

  // Unsaved changes: ask before leaving (Back, menu, closing the tab).
  const dirty =
    JSON.stringify(draft) !== JSON.stringify(EMPTY_CONTACT) ||
    JSON.stringify(answers) !== JSON.stringify(projectFrom());
  const blocker = useBlocker(({ currentLocation, nextLocation }) => {
    return dirty && !saved.current && currentLocation.pathname !== nextLocation.pathname;
  });
  useEffect(() => {
    if (!dirty) return;
    const onLeave = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener('beforeunload', onLeave);
    return () => window.removeEventListener('beforeunload', onLeave);
  }, [dirty]);

  function check(): ContactValues | null {
    const contact = validateContact(draft);
    const lead = project ? projectErrors(shown) : null;
    setErrors(contact.ok ? {} : toFieldErrors(contact.errors));
    setProjectErrs(lead ?? {});
    if (!contact.ok) {
      const first = contact.errors[0]!.field;
      if (!BLOCK_1.includes(first)) setMoreOpen(true);
      setTimeout(() => {
        formRef.current?.querySelector<HTMLElement>(`[id$="-${first}"]`)?.focus();
      });
      return null;
    }
    if (lead) {
      focusFirst(formRef.current, lead);
      return null;
    }
    return contact.value;
  }

  /** Back where the person came from (home when the form was opened directly). */
  function back() {
    if (location.key !== 'default') navigate(-1);
    else navigate('/', { replace: true });
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (saving || known) return;
    const contact = check();
    if (!contact) return;
    setSaving(true);
    setFailed(null);
    try {
      const eventId = await askEvent.eventForCapture();
      if (eventId === undefined) return setSaving(false);
      const changedAt = new Date().toISOString();
      const contactId = newId();
      const ops: SyncOp[] = [
        {
          opId: newId(),
          entity: 'contact',
          id: contactId,
          action: 'upsert',
          changedAt,
          fields: { ...withoutBlanks(contact), source: 'manual', event_id: eventId },
        },
      ];
      if (project) {
        const event = eventId ? await store?.getRecord('event', eventId) : null;
        ops.push(
          ...leadOps({
            person: {
              id: contactId,
              company: contact.company,
              country: contact.country ?? null,
              event_id: eventId,
            },
            draft: shown,
            eventId,
            eventCode: (event?.event_code as string | undefined) ?? null,
            ownerId: user.id,
            qualify: can('leads.qualify'),
            stages,
            changedAt,
          }).ops,
        );
      }
      await save(...ops);
      saved.current = true;
      toast(
        project
          ? `Lead saved: ${contact.company}`
          : `Contact saved: ${contact.first_name} ${contact.last_name}`,
      );
      back();
    } catch {
      setFailed('This device could not save the contact. Nothing was lost: try again.');
      setSaving(false);
    }
  }

  const formId = useId();
  const closing = blocker.state === 'blocked';

  return (
    <Dialog
      title="Add contact"
      onClose={back}
      footer={
        <>
          <button type="button" className="btn" onClick={back}>
            Cancel
          </button>
          <button
            type="submit"
            form={formId}
            className="btn primary"
            disabled={saving || Boolean(known)}
          >
            Save
          </button>
        </>
      }
    >
      {can('contacts.handoff') && (
        <button
          type="button"
          className="btn block"
          style={{ marginBottom: 2 }}
          onClick={() => navigate('/visitor', { replace: true })}
        >
          Hand the tablet to the visitor
        </button>
      )}
      <form
        id={formId}
        ref={formRef}
        className="dlg-form"
        noValidate
        onSubmit={(e) => void submit(e)}
      >
        <ContactForm
          value={draft}
          onChange={(v) => {
            setDraft(v);
            if (Object.keys(errors).length > 0) setErrors({});
          }}
          errors={errors}
          moreOpen={moreOpen}
          onMoreToggle={setMoreOpen}
          leadAnswers={false}
        >
          {known && (
            <KnownContact
              known={known}
              what="Saving would make a second one: open it instead."
              onOpen={(lead) =>
                void knownContactId(known)
                  .then((id) => {
                    if (!id) return;
                    saved.current = true;
                    navigate(lead ? `/people/contacts/${id}/lead` : `/people/contacts/${id}`, {
                      replace: true,
                    });
                  })
                  .catch(() => setFailed('This contact could not be opened. Try again.'))
              }
            />
          )}
          {canLead && (
            <IsThereAProject
              value={project}
              onChange={(yes) => {
                setProject(yes);
                setProjectErrs({});
              }}
            />
          )}
          {project && (
            <ProjectBlock
              value={shown}
              onChange={(v) => {
                if (v.installation_country !== shown.installation_country) setCountryChosen(true);
                setAnswers(v);
                if (Object.keys(projectErrs).length > 0) setProjectErrs({});
              }}
              errors={projectErrs}
              personName={draft.first_name}
            />
          )}
        </ContactForm>
        {failed && <Notice kind="error">{failed}</Notice>}
      </form>
      {closing && (
        <Dialog
          small
          title="Close without saving?"
          onClose={() => blocker.reset?.()}
          footer={
            <>
              <button type="button" className="btn" autoFocus onClick={() => blocker.reset?.()}>
                Keep editing
              </button>
              <button type="button" className="btn primary" onClick={() => blocker.proceed?.()}>
                Close without saving
              </button>
            </>
          }
        >
          <p className="note">Your changes in this form are lost.</p>
        </Dialog>
      )}
      {askEvent.dialog}
    </Dialog>
  );
}

/** Not given: an empty optional field is not sent at all. */
function withoutBlanks(values: ContactValues): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(values).filter(
      ([, v]) => v !== undefined && v !== '' && !(Array.isArray(v) && v.length === 0),
    ),
  );
}
